import { createSignal, onCleanup, onMount } from "solid-js";
import { Outlet, useRouterState } from "@tanstack/solid-router";

import { useTheme } from "@/providers/theme-provider";
import { AppBackground } from "@/components/app-background";
import { AppHeader } from "@/components/app-header";
import { OfflineBanner } from "@/components/offline-banner";
import { AppErrorBoundary } from "@/components/app-error-boundary";
import { useInteractionFlag } from "@fleet/ui";
import { AppLayoutSidebar } from "./app-layout-sidebar";

/**
 * Bóveda shell: background + sidebar + header + Outlet. All vault
 * state (lock/keys) lives in <VaultProvider> (mounted above); this layer
 * only draws static, instant chrome around content.
 */
export function AppLayoutContent() {
  const theme = useTheme();
  const [isSidebarOpen, setIsSidebarOpen] = createSignal(false);
  const routerState = useRouterState();
  const isNavigating = () => routerState().status === "pending";

  onMount(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isSidebarOpen()) {
        setIsSidebarOpen(false);
      }
    };
    globalThis.addEventListener("keydown", handleEscape);
    onCleanup(() => {
      globalThis.removeEventListener("keydown", handleEscape);
    });
  });

  // With the nav drawer open, the horizontal gesture belongs to it (drag to
  // dismiss). This repo has no page-level swipe gesture yet, but the flag is
  // the declaration of who owns the horizontal gesture regardless — the day
  // one is added, it stands down without further wiring.
  useInteractionFlag({ "nav-drawer.open": isSidebarOpen });

  return (
    <AppBackground
      class="flex h-dvh overflow-hidden text-foreground"
      patternType={theme.tokens()?.patternType}
      patternOpacity={theme.tokens()?.patternOpacity}
    >
      <AppLayoutSidebar isOpen={isSidebarOpen} setOpen={setIsSidebarOpen} />

      <main class="flex flex-1 flex-col overflow-hidden bg-transparent pt-[var(--safe-area-inset-top)] pr-[var(--safe-area-inset-right)] pb-[var(--safe-area-inset-bottom)]">
        <AppHeader onMenuClick={() => setIsSidebarOpen(true)} isNavigating={isNavigating} />
        <OfflineBanner />
        <div class="flex min-h-0 flex-1 flex-col overflow-y-auto pl-[var(--safe-area-inset-left)] pr-[var(--safe-area-inset-right)]">
          <AppErrorBoundary inline label="route">
            <Outlet />
          </AppErrorBoundary>
        </div>
      </main>
    </AppBackground>
  );
}
